'use client'

import { Plus12, X12 } from '@vinted/monochrome-icons'
import { Button, Container, Icon, Text } from '@vinted/web-ui'
import { useEffect, useMemo, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { getPrivacyPolicy } from '@marketplace-web/user-agreements/privacy-center-data'

import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

import BackToTopButton from '../../components/BackToTopButton'

// This page will be removed in the future, once the new Privacy Policy page is fully implemented

const PrivacyPolicy = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  const translate = useTranslate()

  const { fetch: fetchPrivacyPolicy, data, isLoading } = useFetch(getPrivacyPolicy)

  useEffect(() => {
    fetchPrivacyPolicy()
  }, [fetchPrivacyPolicy])

  const privacyPolicyContent = useMemo(() => {
    if (!data?.body) {
      return undefined
    }

    const updateDetailsElement = (detailsElement: HTMLDetailsElement) => {
      if (!isExpanded) {
        detailsElement.removeAttribute('open')

        return
      }

      detailsElement.setAttribute('open', 'true')
    }

    const parser = new DOMParser()
    const serializer = new XMLSerializer()
    const document = parser.parseFromString(data.body, 'text/html')
    const allDetailsElements = document.querySelectorAll('details')

    allDetailsElements.forEach(updateDetailsElement)

    return serializer.serializeToString(document)
  }, [data, isExpanded])

  const handleButtonClick = () => {
    setIsExpanded(currentState => !currentState)
  }

  const renderExpandAllButton = () => {
    if (!privacyPolicyContent) return null

    const buttonText = isExpanded
      ? translate('privacy_policy_details_button.collapse_all')
      : translate('privacy_policy_details_button.expand_all')

    return (
      <div className="u-ui-padding-top-x2-large u-ui-padding-left-medium">
        <Button
          text={buttonText}
          styling={Button.Styling.Flat}
          size={Button.Size.Small}
          onClick={handleButtonClick}
          icon={<Icon name={isExpanded ? X12 : Plus12} color={Icon.Color.Primary} />}
          inline
        />
      </div>
    )
  }

  if (isLoading) {
    return <ContentLoader testId="privacy-policy-loader" />
  }

  return (
    <article className="privacy-policy-content user-created-content">
      {renderExpandAllButton()}
      <Container styling={Container.Styling.Wide}>
        <Text as="span" text={privacyPolicyContent} html />
      </Container>
      <BackToTopButton />
    </article>
  )
}

export default PrivacyPolicy
